// @ts-strict-ignore
import React, { useEffect, useMemo, useRef } from 'react'
import isEqual from 'lodash-es/isEqual'
import { useTranslation } from 'react-i18next'
import {
  Box,
  NoData,
  NoResults,
  Pagination,
  RequestError,
  RowsSkeletonLoader,
  Box as Wrapper,
} from '@node-space/ui-components'
import FilterBar from 'components/FilterBar/FilterBar'
import { PageHeader } from 'components/header/PageHeader'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useApiPaginationWithPaymentsList } from 'hooks/useApiPaginationWithPaymentsList'
import { useMappedRoles } from 'hooks/useMappedRoles'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { PaymentsResponse, PaymentStatus, PaymentTag } from '../../types/payments'
import SearchFields from './components/Form/SearchFields'
import { CryptoPaymentsHeaderButtons } from './components/Header/CryptoPaymentsHeaderButtons'
import PaymentTable from './components/Table/PaymentTable'
import usePaymentsApi from './hooks/usePaymentsApi'

export const CryptoPayments = () => {
  const { t } = useTranslation()
  const permissions = useMappedRoles()?.payments

  const canEdit = permissions?.canEdit
  const canCreatePayIn = permissions?.canCreatePayIn
  const isInitialized = useRef(false)

  const {
    executor,
    isLoading,
    isError,
    data: {
      paymentsList: { transactions, paging, pagingMax },
      searchByRef,
      searchByTransactionHash,
      searchByUUID,
      isExportingCSV,
      dateValue,
      initialDateValue,
      merchant,
      status,
      tag,
    },
    actions: {
      onDateSelected,
      handleExportClicked,
      onPaymentStatusChanged,
      onPaymentTagChange,
      onMerchantSelected,
      showSearchResult,
      onResetAll,
      setPaymentsList,
      onPageChange,
      onSearchByRefChanged,
      onSearchByTransactionHash,
      onSearchByUUID,
    },
  } = usePaymentsApi()

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useApiPaginationWithPaymentsList({
      onPageChange,
      setPaymentsList,
      executor,
      pagingMax,
    })

  const urlParams = new URLSearchParams(window.location.search)
  const queryPaymentsLinkBy = urlParams.get('dlt-reference') ?? null

  useEffect(() => {
    return () => {
      onResetAll()
    }
  }, [])

  useEffect(() => {
    if (queryPaymentsLinkBy && !isLoading && isInitialized?.current) {
      onSearchByRefChanged(queryPaymentsLinkBy)
    }
  }, [isInitialized?.current])

  const isUsingFilters = useMemo(() => {
    return (
      !!searchByRef ||
      !!searchByTransactionHash ||
      !!searchByUUID ||
      merchant !== 'ALL' ||
      status !== PaymentStatus.ALL ||
      tag !== PaymentTag.ALL ||
      !isEqual(dateValue, initialDateValue)
    )
  }, [
    searchByRef,
    searchByTransactionHash,
    searchByUUID,
    status,
    tag,
    dateValue,
    initialDateValue,
    merchant,
  ])

  const showNoSearchResults = isUsingFilters && !isLoading && !transactions?.length
  const showHeader = !!transactions?.length || isUsingFilters

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.PAYMENTS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  const getPageHeader = () => {
    return t('payments.headerDescriptionTravelRuleV2')
  }

  return (
    <Wrapper className="payments-container">
      {(canEdit || canCreatePayIn) && showHeader && (
        <PageHeader
          description={getPageHeader()}
          renderRightSection={<CryptoPaymentsHeaderButtons refetch={executor} />}
        />
      )}

      {showHeader && (
        <FilterBar
          eventHandlers={{
            executor,
            onMerchantSelected,
            onDateFilterChange: onDateSelected,
            onPaymentStatusChanged,
            handleExportClicked,
            onResetAll,
            showSearchResult,
            onPaymentTagChange,
          }}
          conditions={{ isExportingCSV }}
          filterDateRange={dateValue}
        >
          <SearchFields
            t={t}
            searchByRef={searchByRef}
            searchByTransactionHash={searchByTransactionHash}
            searchByUUID={searchByUUID}
            onSearchByRefChanged={onSearchByRefChanged}
            onSearchByTransactionHash={onSearchByTransactionHash}
            onSearchByUUID={onSearchByUUID}
          />
        </FilterBar>
      )}

      {isLoading && !isError && (
        <Box paddingT={20}>
          <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
        </Box>
      )}

      {!isLoading && !transactions?.length && !isUsingFilters && (
        <NoData
          dataName="transactions"
          description={t('payments.emptyStateCryptoPaymentsDescription')}
          PrimaryButton={
            (canEdit || canCreatePayIn) && <CryptoPaymentsHeaderButtons refetch={executor} />
          }
        />
      )}

      {isError && !isLoading && <RequestError />}

      {showNoSearchResults && <NoResults onClear={onResetAll} />}

      {!!transactions?.length && !isError && !isLoading && (
        <PaymentTable
          transactions={transactions as unknown as PaymentsResponse[]}
          isLoading={isLoading}
        />
      )}
      {!isError && !!paging?.total && !isLoading && (
        <Pagination
          testId="payments-pagination"
          page={page}
          rowsPerPage={rowsPerPage}
          total={paging.total}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}
    </Wrapper>
  )
}
